import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserStore } from 'app/core/services/user.store';

/**
 * Quick-switch menu for the last ten impersonated users. Stays available during
 * an impersonation (see `showForOriginalAdmin`), so an admin can hop from one
 * user to the next without going back to the users list first.
 */
@Component({
  selector: 'app-impersonation-switcher',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatDividerModule, MatIconModule, MatMenuModule, MatTooltipModule],
  template: `
    @if (hasMenu()) {
      <button
        mat-icon-button
        [matMenuTriggerFor]="switcherMenu"
        matTooltip="Gebruiker overnemen"
        data-cy="impersonation-switcher-button">
        <mat-icon svgIcon="mat_outline:switch_account"></mat-icon>
      </button>

      <mat-menu #switcherMenu="matMenu">
        <div class="px-4 pt-3 pb-1 text-sm font-medium">Recent overgenomen</div>

        @for (user of recentUsers(); track user.userId; let index = $index) {
          <button
            mat-menu-item
            [disabled]="user.userId === userStore.selectedUserId()"
            (click)="userStore.switchImpersonation(user.userId)"
            [attr.data-cy]="'recent-impersonation-' + index">
            <div class="flex flex-col leading-tight py-1">
              <span class="truncate">{{ user.label }}</span>
              @if (user.subLabel) {
                <span class="text-accent text-xs truncate">{{ user.subLabel }}</span>
              }
            </div>
          </button>
        } @empty {
          <div class="px-4 py-2 text-accent text-sm">Nog geen overnames</div>
        }

        <mat-divider></mat-divider>

        @if (userStore.impersonationData()) {
          <button mat-menu-item (click)="userStore.stopImpersonating()" data-cy="switcher-stop-impersonating">
            <div class="flex items-center gap-2 py-1">
              <mat-icon class="icon-size-5 shrink-0" svgIcon="heroicons_solid:arrow-uturn-left"></mat-icon>
              <div class="flex flex-col leading-tight">
                <span>Terug naar eigen account</span>
                @if (originalUserLabel()) {
                  <span class="text-accent text-xs truncate">{{ originalUserLabel() }}</span>
                }
              </div>
            </div>
          </button>
        }

        @if (recentUsers().length) {
          <button mat-menu-item (click)="userStore.clearRecentImpersonations()" data-cy="clear-recent-impersonations">
            <span class="text-accent">Lijst wissen</span>
          </button>
        }
      </mat-menu>
    }
  `,
})
export class ImpersonationSwitcherComponent {
  readonly userStore = inject(UserStore);

  /**
   * Users are not guaranteed to have a name, so fall back to the e-mail address
   * and only show a second line when it adds information.
   */
  readonly recentUsers = computed(() =>
    this.userStore.recentImpersonations().map(user => {
      const name = user.name?.trim();
      const emailAddress = user.emailAddress?.trim();
      return {
        userId: user.userId,
        label: name || emailAddress || 'Onbekende gebruiker',
        subLabel: name ? emailAddress : null,
      };
    })
  );

  /** The real account behind the impersonation, so "back" names where you land */
  readonly originalUserLabel = computed(() => {
    const originalUser = this.userStore.originalUser();
    return originalUser?.name?.trim() || originalUser?.emailAddress?.trim() || '';
  });

  /** Hide the button entirely until there is something to switch to */
  readonly hasMenu = computed(() => this.recentUsers().length > 0 || !!this.userStore.impersonationData());
}
